import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { API_URL } from "../../../config";
import "./AdminLeavePage.css";

function AdminLeavePage() {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());

  const [showModal, setShowModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [paid, setPaid] = useState(false);
  const [adminNote, setAdminNote] = useState("");

  const loadLeaves = () => {
    axios
      .get(`${API_URL}/api/admin/getAllLeave`)
      .then((res) => {
        setLeaves(res.data);
        setLoading(false);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    loadLeaves();
    const interval = setInterval(loadLeaves, 15000);
    const onFocus = () => loadLeaves();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const updateLeave = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/updateLeaveStatus/${id}`, {
        status,
        paid,
        adminNote,
      });

      alert(`Leave ${status} Successfully`);
      setShowModal(false);
      loadLeaves();
    } catch (err) {
      alert("Error updating leave");
    }
  };

  const filteredLeaves = leaves.filter((lv) => {
    const d = new Date(lv.from_date);
    return d.getMonth() === month && d.getFullYear() === year;
  });

  const stats = useMemo(() => {
    const total = filteredLeaves.length;
    const approved = filteredLeaves.filter((lv) => lv.status === "Approved").length;
    const rejected = filteredLeaves.filter((lv) => lv.status === "Rejected").length;
    const pending = filteredLeaves.filter((lv) => lv.status === "Pending").length;
    const paidCount = filteredLeaves.filter((lv) => lv.paid).length;
    const unpaidCount = total - paidCount;
    const totalDays = filteredLeaves.reduce((sum, lv) => sum + Number(lv.days || 0), 0);
    const avgDays = total > 0 ? (totalDays / total).toFixed(1) : "0.0";

    return {
      total,
      approved,
      rejected,
      pending,
      paidCount,
      unpaidCount,
      totalDays,
      avgDays,
    };
  }, [filteredLeaves]);

  const getBadge = (status) => {
    if (status === "Approved") return "badge bg-success";
    if (status === "Rejected") return "badge bg-danger";
    return "badge bg-warning text-dark";
  };

  const exportExcel = () => {
    const rowsForExport = filteredLeaves.map((lv) => ({
      Employee: lv.employeeId?.ename || "",
      Employee_Email: lv.employeeId?.email || "",
      Leave_ID: lv.leaveId || "",
      Type: lv.leave_type || "",
      Category: lv.leave_category || "",
      From: lv.from_date ? new Date(lv.from_date).toLocaleDateString() : "",
      To: lv.to_date ? new Date(lv.to_date).toLocaleDateString() : "",
      Days: Number(lv.days || 0),
      Employee_Reason: lv.reason || "-",
      Admin_Note: lv.adminNote && lv.adminNote.trim() !== "" ? lv.adminNote : "-",
      Paid: lv.paid ? "Paid" : "Unpaid",
      Status: lv.status || "",
    }));

    const sheet = XLSX.utils.json_to_sheet(rowsForExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Leaves");
    XLSX.writeFile(workbook, `Admin_Leaves_${month + 1}_${year}.xlsx`);
  };

  return (
    <div className="admin-leave-page">
      <div className="admin-leave-shell">
        <div className="admin-leave-hero">
          <div>
            <p className="admin-leave-eyebrow">HR Operations</p>
            <h3>Admin Leave Panel</h3>
            <span>Review requests, payment status, and month-wise leave logs</span>
          </div>
        </div>

        <div className="admin-leave-filters">
          <select
            className="form-select admin-leave-select"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {[
              "January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December"
            ].map((m, i) => (
              <option key={i} value={i}>{m}</option>
            ))}
          </select>

          <select
            className="form-select admin-leave-select"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <option key={i} value={new Date().getFullYear() - i}>
                {new Date().getFullYear() - i}
              </option>
            ))}
          </select>

          <button type="button" className="btn admin-leave-export-btn" onClick={exportExcel}>
            Export Excel
          </button>
        </div>

        <div className="admin-leave-stats-grid">
          <div className="admin-leave-stat-card">
            <p>Total Requests</p>
            <h4>{stats.total}</h4>
          </div>
          <div className="admin-leave-stat-card">
            <p>Pending</p>
            <h4>{stats.pending}</h4>
          </div>
          <div className="admin-leave-stat-card">
            <p>Approved</p>
            <h4>{stats.approved}</h4>
          </div>
          <div className="admin-leave-stat-card">
            <p>Rejected</p>
            <h4>{stats.rejected}</h4>
          </div>
          <div className="admin-leave-stat-card">
            <p>Paid / Unpaid</p>
            <h4>{stats.paidCount} / {stats.unpaidCount}</h4>
          </div>
          <div className="admin-leave-stat-card">
            <p>Total Days (Avg)</p>
            <h4>{stats.totalDays} ({stats.avgDays})</h4>
          </div>
        </div>

        <div className="admin-leave-table-card">
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Leave ID</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Days</th>
                  <th>Employee Reason</th>
                  <th>Admin Note</th>
                  <th>Paid</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="12" className="text-center">Loading...</td>
                  </tr>
                ) : filteredLeaves.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="text-center">No Leaves for this month</td>
                  </tr>
                ) : (
                  filteredLeaves.map((lv) => (
                    <tr key={lv._id}>
                      <td>
                        <strong>{lv.employeeId?.ename}</strong><br />
                        <small>{lv.employeeId?.email}</small>
                      </td>

                      <td>{lv.leaveId}</td>
                      <td>{lv.leave_type}</td>
                      <td>{lv.leave_category}</td>

                      <td>{new Date(lv.from_date).toLocaleDateString()}</td>
                      <td>{new Date(lv.to_date).toLocaleDateString()}</td>

                      <td>{lv.days}</td>

                      <td>{lv.reason || "-"}</td>

                      <td>
                        {lv.adminNote && lv.adminNote.trim() !== "" ? (
                          lv.adminNote
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>

                      <td>
                        <span className={lv.paid ? "badge bg-success" : "badge bg-danger"}>
                          {lv.paid ? "Paid" : "Unpaid"}
                        </span>
                      </td>

                      <td>
                        <span className={getBadge(lv.status)}>
                          {lv.status}
                        </span>
                      </td>

                      <td>
                        {lv.status === "Pending" ? (
                          <button
                            className="btn btn-sm admin-leave-review-btn"
                            onClick={() => {
                              setSelectedLeave(lv);
                              setPaid(lv.paid);
                              setAdminNote(lv.adminNote || "");
                              setShowModal(true);
                            }}
                          >
                            Review
                          </button>
                        ) : (
                          <em>No Action</em>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && selectedLeave && (
        <div className="modal-overlay-custom">
          <div className="modal-box-custom admin-leave-modal">
            <h4>Review Leave Request</h4>
            <p>
              <strong>Employee:</strong> {selectedLeave.employeeId?.ename}<br />
              <strong>Leave ID:</strong> {selectedLeave.leaveId}<br />
              <strong>Days:</strong> {selectedLeave.days}<br />
            </p>

            <label className="form-label mt-2">Mark as Paid</label>
            <select
              className="form-select admin-leave-select"
              value={paid}
              onChange={(e) => setPaid(e.target.value === "true")}
            >
              <option value="false">Unpaid</option>
              <option value="true">Paid</option>
            </select>

            <label className="form-label mt-3">Admin Note</label>
            <textarea
              className="form-control admin-leave-textarea"
              rows="3"
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Write a note for employee..."
            ></textarea>

            <div className="d-flex justify-content-between mt-4 gap-2 flex-wrap">
              <button
                className="btn admin-leave-action approve"
                onClick={() => updateLeave(selectedLeave._id, "Approved")}
              >
                Approve
              </button>

              <button
                className="btn admin-leave-action reject"
                onClick={() => updateLeave(selectedLeave._id, "Rejected")}
              >
                Reject
              </button>

              <button
                className="btn admin-leave-action cancel"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminLeavePage;
